import { DragDropProvider } from '@dnd-kit/react'
import { useSortable } from '@dnd-kit/react/sortable'
import { IconArrowsSort, IconChevronDown, IconChevronUp, IconGripVertical } from '@tabler/icons-react'
import { useState } from 'react'

import { AstraButton, AstraDialog, AstraIcon, AstraParagraph, AstraPractitionerChip, AstraSelect, AstraTooltip } from '~/astra'
import { useActiveDepartment, useDepartments } from '~/storm'

import { StatusBadge } from '../SurgeonsTable'
import { ReorderRow, useReorderPractitioners } from './useReorderPractitioners'

const SortablePractitionerRow = ({
    row,
    index,
    count,
    onMoveClick,
}: {
    row: ReorderRow
    index: number
    count: number
    onMoveClick: (from: number, to: number) => void
}) => {
    const { ref, handleRef, isDragging } = useSortable({ id: row.uuid, index })
    return (
        <div
            ref={ref}
            className={`group flex items-center justify-between gap-3 rounded-md border px-3 py-2 transition ${
                isDragging ? 'border-info-ring bg-surface-strong shadow-sm' : 'border-strong bg-surface-base hover:bg-surface-strong'
            }`}
        >
            <div className="grid flex-1 grid-cols-[auto_5rem_1fr_auto] items-center" ref={handleRef}>
                <button className="cursor-grab text-subtle active:cursor-grabbing" aria-label={`Dra ${row.short_name} for å endre rekkefølge`}>
                    <AstraIcon icon={IconGripVertical} />
                </button>
                <AstraPractitionerChip shortName={row.short_name} title={null} variant="ghost" />
                <AstraParagraph variant="body" className="select-none truncate text-default">
                    {row.fullName}
                </AstraParagraph>
                {row.status === 'inactive' && <StatusBadge status={row.status} />}
            </div>
            <div className="flex gap-0.5 opacity-0 transition focus-within:opacity-100 group-hover:opacity-100">
                <AstraButton
                    variant="ghost"
                    size="small"
                    aria-label={`Flytt ${row.short_name} opp`}
                    icon={<AstraIcon icon={IconChevronUp} />}
                    disabled={index === 0}
                    onClick={() => onMoveClick(index, index - 1)}
                />
                <AstraButton
                    variant="ghost"
                    size="small"
                    aria-label={`Flytt ${row.short_name} ned`}
                    icon={<AstraIcon icon={IconChevronDown} />}
                    disabled={index === count - 1}
                    onClick={() => onMoveClick(index, index + 1)}
                />
            </div>
        </div>
    )
}

export const ReorderSurgeonsContent = ({ onClose }: { onClose: () => void }) => {
    const activeDepartment = useActiveDepartment()
    const departments = useDepartments()

    const defaultDepartment = activeDepartment === 'Alle' ? departments[0] : activeDepartment
    const [selectedDepartment, setSelectedDepartment] = useState(defaultDepartment)

    const { rows, isDirty, handleMove, handleDragOver, mutation: saveMutation } = useReorderPractitioners()

    // this should never happen due to defaulting to first department - but used as a type guard
    if (!selectedDepartment) return null

    const currentRows = rows.get(selectedDepartment.uuid) ?? []

    const handleSelectDepartment = (uuid: string | undefined) => {
        const newDepartment = departments.find(d => d.uuid === uuid)
        if (newDepartment) {
            setSelectedDepartment(newDepartment)
        }
    }

    const handleSave = async () => {
        await saveMutation.mutateAsync()
        onClose()
    }

    return (
        <div className="flex max-h-[70vh] max-w-[720px] flex-col gap-4">
            <AstraParagraph variant="body-small" className="text-subtle">
                Rekkefølgen bestemmer hvordan kirurgene sorteres i den valgte avdelingen i bemanningsplanen.
            </AstraParagraph>
            <AstraSelect
                label="Avdeling"
                value={selectedDepartment.uuid}
                onChange={handleSelectDepartment}
                options={departments.map(d => ({ label: d.name, value: d.uuid }))}
            />
            <div className="flex flex-col gap-2 overflow-y-auto">
                <DragDropProvider onDragOver={event => handleDragOver(selectedDepartment.uuid, event)}>
                    {currentRows.map((row, index) => (
                        <SortablePractitionerRow
                            key={row.uuid}
                            row={row}
                            index={index}
                            count={currentRows.length}
                            onMoveClick={(from, to) => handleMove(selectedDepartment.uuid, from, to)}
                        />
                    ))}
                </DragDropProvider>
            </div>
            <div className="flex justify-end gap-2">
                <AstraButton variant="critical-secondary" label="Avbryt" onClick={onClose} disabled={saveMutation.isPending} />
                <AstraTooltip hidden={isDirty} tooltipContent="Du må endre rekkefølgen før du kan lagre ny rekkefølge.">
                    <AstraButton
                        variant="primary"
                        label="Lagre"
                        onClick={handleSave}
                        disabled={!isDirty || saveMutation.isPending}
                        isLoading={saveMutation.isPending}
                    />
                </AstraTooltip>
            </div>
        </div>
    )
}

export const ReorderSurgeonsModal = ({ onClose, isOpen }: { onClose: () => void; isOpen: boolean }) => (
    <AstraDialog
        isOpen={isOpen}
        onCloseDialog={onClose}
        title="Endre rekkefølge på kirurger"
        header={
            <div className="flex items-center gap-2">
                <AstraIcon icon={IconArrowsSort} />
                <AstraParagraph weight="medium" className="text-default">
                    Endre rekkefølge på kirurger
                </AstraParagraph>
            </div>
        }
    >
        <ReorderSurgeonsContent onClose={onClose} />
    </AstraDialog>
)
